import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import * as pdfjs from 'pdfjs-dist';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';
import CustomModalFooter from 'src/components/ReportViewer/CustomModalFooter';
import OrderTaskList from 'src/components/ReportViewer/OrderTasks/OrderTaskList';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import { usePreparePdf } from 'src/hooks/usePreparePdf';

import { captureUserActionError } from 'src/utils/errorReporting';

import 'pdfjs-dist/build/pdf.mjs';
import 'pdfjs-dist/web/pdf_viewer.css';
import PdfViewer from './PdfViewer';

// Copy file from node_modules/pdfjs-dist/legacy/build/pdf.worker.min.mjs to public/js folder
pdfjs.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.mjs';

if (Promise.withResolvers === undefined) {
  if (window) {
    // @ts-expect-error This does not exist outside of polyfill which this is doing
    window.Promise.withResolvers = function () {
      let resolve, reject;
      const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
      });
      return { promise, resolve, reject };
    };
  } else {
    // @ts-expect-error This does not exist outside of polyfill which this is doing
    global.Promise.withResolvers = function () {
      let resolve, reject;
      const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
      });
      return { promise, resolve, reject };
    };
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      // Resolve with the Base64 string (removing the prefix)
      const base64data = reader.result as string;
      resolve(base64data.split(',')[1]); // Returns the Base64 part only
    });

    reader.addEventListener('error', () => {
      reject(new Error('Failed to convert blob to Base64'));
    });

    reader.readAsDataURL(blob); // Read the Blob as a data URL
  });
}

const downloadFile = async (url: string, filename: string, blob: Blob) => {
  // Use client api?
  if (window._cdvElectronIpc?.saveReportFile) {
    const encoded = await blobToBase64(blob);
    window._cdvElectronIpc.saveReportFile(filename, encoded);
    return;
  }

  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

// Check if the pdf viewer is enabled in the browser
// Will use the pdf viewer if it is enabled, otherwise will use pdf.js as fallback
function isPdfViewerEnabled(): boolean {
  return navigator.pdfViewerEnabled;
}

interface IReportViewerProps {
  show: boolean;
  handleCloseReportViewer: () => void;
  handleRefreshOrders: () => void;
}

const ReportViewer = ({
  show,
  handleCloseReportViewer,
  handleRefreshOrders,
}: IReportViewerProps) => {
  const {
    session,
    userSettings: { userPermission },
  } = useRemoteBoundStore();

  const {
    i18n: { language },
  } = useTranslation();

  const { t } = useTranslation(I18NextNS.COMMON);

  const { pdfUrl, blobData } = usePreparePdf(handleCloseReportViewer);

  const handlePrintReport = async () => {
    try {
      const allowed = userPermission.print;

      if (allowed !== true) {
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: t('message.error.reportPrintPermissionError'),
          locale: language,
          type: 'error',
        });
        return;
      }

      // use client api?
      if (window._cdvElectronIpc?.printReportFile) {
        // fun printReportFile(base64String: String): Boolean
        const encoded = await blobToBase64(blobData!);
        window._cdvElectronIpc.printReportFile(encoded);
        return;
      }

      // use embedded pdf viewer?
      if (isPdfViewerEnabled()) {
        const reportFrame = document.querySelector(
          '#report-viewer-frame',
        ) as HTMLIFrameElement;
        reportFrame.contentWindow?.print();
        return;
      }

      // print pdf canvas with jQuery printThis plugin
      window.$('canvas.react-pdf__Page__canvas').printThis({
        importCSS: false,
        formValues: false,
        removeScripts: true,
        canvas: true,
        debug: true, // Keep iframe for print preview
        loadCSS: '/css/print.css', // Css file for print layout
      });
    } catch (error) {
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message:
          t('message.error.reportPrintError') +
          ' ' +
          Api.formatException((error as Error).message),
        locale: language,
        type: 'error',
      });

      captureUserActionError(error, 'report_print', {
        customMessage: 'Error printing report',
      });
    }
  };

  const handleDownloadReport = async () => {
    if (session.iid === undefined) {
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: t(`message.error.reportDownloadSessionError`),
        locale: language,
        type: 'error',
      });
      return;
    }

    try {
      const allowed = userPermission.print;

      if (allowed !== true) {
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: t(`message.error.reportDownloadPermissionError`),
          locale: language,
          type: 'error',
        });
        return;
      }

      const title = `${t('report.Report')} ${session.id} ${moment().format('YYYY-MM-DD')}`;

      downloadFile(pdfUrl, title + '.pdf', blobData!);
    } catch (error) {
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message:
          t('message.error.reportDownloadError') +
          ' ' +
          Api.formatException((error as Error).message),
        locale: language,
        type: 'error',
      });
    }
  };

  return (
    <CustomModal
      show={show}
      onHide={() => {
        handleCloseReportViewer();
      }}
      centered
      size="xl"
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body className="report-content w-100 vh-100 d-flex p-0">
        <PdfViewer isPdfViewerEnabled={isPdfViewerEnabled} pdfUrl={pdfUrl} />
        <OrderTaskList />
      </Modal.Body>

      <CustomModalFooter
        handleDownloadReport={handleDownloadReport}
        handlePrintReport={handlePrintReport}
        handleCloseReportViewer={handleCloseReportViewer}
        handleRefreshOrders={handleRefreshOrders}
        blobData={blobData}
      />
    </CustomModal>
  );
};

export default ReportViewer;
